import i18n from 'i18n-literally'

import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import type {
  ApnsTokenReceivedEvent,
  FirebaseMessagingPlugin,
  NotificationActionPerformedEvent,
  NotificationReceivedEvent,
  TokenReceivedEvent,
} from '@capacitor-firebase/messaging'
import { Capacitor } from '@capacitor/core'
import type { PermissionState, PluginListenerHandle } from '@capacitor/core'
import { Device } from '@capacitor/device'
import type { DevicePlugin } from '@capacitor/device'
import { LocalNotifications } from '@capacitor/local-notifications'
import type { ActionPerformed, LocalNotificationsPlugin } from '@capacitor/local-notifications'

import { getRoomAnnotationURL } from '~/src/Lib/Utils'

import { requireNativePlatform } from './platform'

type UnknownRecord = Record<string, unknown>
type Messaging = Pick<
  FirebaseMessagingPlugin,
  'addListener' | 'checkPermissions' | 'deleteToken' | 'getToken' | 'requestPermissions'
>
type Notifications = Pick<LocalNotificationsPlugin, 'addListener' | 'createChannel' | 'schedule'>

export interface ParsedPushData {
  readonly dataType: string | null
  readonly facility: number | null
  readonly room: number | null
  readonly title: string
  readonly ts: string | null
  readonly zones: readonly number[]
}

export interface PushNavigation {
  readonly deviceStateFilter?: 'offline'
  readonly url: string
}

export interface NativePushResult {
  readonly permission: PermissionState
  readonly token: string | null
}

let pushListeners: PluginListenerHandle[] | null = null
const ALERT_CHANNEL_ID = 'aroya-alerts'

const isRecord = (value: unknown): value is UnknownRecord =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const assertRecord = (value: unknown, name: string): UnknownRecord => {
  if (!isRecord(value)) {
    throw new TypeError(`${name} must be an object.`)
  }
  return value
}

const optionalInteger = (value: unknown, name: string): number | null => {
  if (value === undefined || value === null || value === '' || value === 'None') return null
  const parsed = Number(value)
  if (!Number.isInteger(parsed)) throw new TypeError(`${name} must be an integer.`)
  return parsed
}

const optionalString = (value: unknown, name: string): string | null => {
  if (value === undefined || value === null || value === '' || value === 'None') return null
  if (typeof value !== 'string') throw new TypeError(`${name} must be a string.`)
  return value
}

const parseZones = (value: unknown): readonly number[] => {
  if (value === undefined || value === null || value === 'None') return []
  const rawZones = Array.isArray(value) ? value : [value]
  return rawZones.map(zone => {
    const parsed = optionalInteger(zone, 'Push notification zone')
    if (parsed === null) throw new TypeError('Push notification zone must be an integer.')
    return parsed
  })
}

const notificationId = (notification: UnknownRecord): number => {
  const source =
    notification.id ?? JSON.stringify([notification.title ?? '', notification.body ?? '', notification.data ?? {}])
  let hash = 2166136261
  for (const character of String(source)) {
    hash ^= character.charCodeAt(0)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 1 || 1
}

export const presentAndroidPushNotification = async (
  notification: unknown,
  localNotifications: Pick<Notifications, 'schedule'> = LocalNotifications,
): Promise<void> => {
  const message = assertRecord(notification, 'Push notification')
  const data = assertRecord(message.data ?? {}, 'Push notification data')
  const title = optionalString(message.title ?? data.title, 'Push notification title') ?? i18n`AROYA alert`
  const body = optionalString(message.body ?? data.body, 'Push notification body') ?? ''
  await localNotifications.schedule({
    notifications: [
      {
        id: notificationId(message),
        title,
        body,
        channelId: ALERT_CHANNEL_ID,
        extra: { ...data, title },
      },
    ],
  })
}

export const parsePushData = (value: unknown): Readonly<ParsedPushData> => {
  const data = assertRecord(value, 'Push notification data')
  const ts = optionalString(data.ts, 'Push notification timestamp')
  if (ts && Number.isNaN(Date.parse(ts))) throw new TypeError('Push notification timestamp must be ISO-8601.')

  return Object.freeze({
    title: optionalString(data.title, 'Push notification title') ?? '',
    facility: optionalInteger(data.facility, 'Push notification facility'),
    room: optionalInteger(data.room, 'Push notification room'),
    zones: Object.freeze(parseZones(data.zone)),
    dataType: optionalString(data.data_type_key, 'Push notification data type'),
    ts,
  })
}

export const getPushNavigation = (rawData: unknown, currentFacilityId: unknown): Readonly<PushNavigation> => {
  const data = parsePushData(rawData)
  const facilityId = data.facility ?? currentFacilityId
  if (!Number.isInteger(facilityId)) throw new TypeError('Push notification does not identify a facility.')

  if (data.title.includes('Device Disconnected') || data.title.includes('Gateway Disconnected')) {
    return Object.freeze({ url: `/f/${facilityId}/setup/devices`, deviceStateFilter: 'offline' })
  }

  if (!data.room) return Object.freeze({ url: `/f/${facilityId}/dashboard` })
  const path = getRoomAnnotationURL({
    roomID: data.room,
    zoneID: data.zones[0] ?? null,
    entity: data.dataType ? ['dataTypes', data.dataType] : [],
    ts: data.ts ?? Date.now(),
  })
  return Object.freeze({ url: `/f/${facilityId}${path}` })
}

interface InitializeNativePushOptions {
  readonly getPlatform?: () => string
  readonly localNotifications?: Notifications
  readonly messaging?: Messaging
  readonly onError: (error: unknown) => void
  readonly onNotificationAction: (data: UnknownRecord) => unknown
  readonly onToken: (token: string) => unknown
  readonly requireNative?: (feature: string) => void
}

export const initializeNativePush = async ({
  onNotificationAction,
  onError,
  onToken,
  localNotifications = LocalNotifications,
  messaging = FirebaseMessaging,
  getPlatform = () => Capacitor.getPlatform(),
  requireNative = requireNativePlatform,
}: InitializeNativePushOptions): Promise<Readonly<NativePushResult>> => {
  requireNative('Push notifications')
  if (typeof onNotificationAction !== 'function' || typeof onError !== 'function' || typeof onToken !== 'function') {
    throw new TypeError('Push notification callbacks must be functions.')
  }

  const checked = await messaging.checkPermissions()
  const requestable = ['prompt', 'prompt-with-rationale'].includes(checked.receive)
  const permission = requestable ? (await messaging.requestPermissions()).receive : checked.receive
  if (permission !== 'granted') return Object.freeze({ permission, token: null })

  if (!pushListeners) {
    const reportAsyncError = (promise: unknown): void => void Promise.resolve(promise).catch(onError)
    const handleEvent =
      <Event>(callback: (event: Event) => unknown) =>
      (event: Event): void => {
        try {
          reportAsyncError(callback(event))
        } catch (error) {
          onError(error)
        }
      }
    const platform = getPlatform()
    const listeners = [
      messaging.addListener(
        'tokenReceived',
        handleEvent(({ token }: TokenReceivedEvent) => {
          if (typeof token !== 'string' || !token) throw new TypeError('Firebase returned an invalid messaging token.')
          return onToken(token)
        }),
      ),
      messaging.addListener(
        'notificationActionPerformed',
        handleEvent(({ notification }: NotificationActionPerformedEvent) => {
          const data = assertRecord(notification.data, 'Push notification data')
          return onNotificationAction({ ...data, title: data.title ?? notification.title })
        }),
      ),
    ]
    if (platform === 'ios') {
      listeners.push(
        messaging.addListener(
          'apnsTokenReceived',
          handleEvent(({ token: apnsToken }: ApnsTokenReceivedEvent) => {
            if (typeof apnsToken !== 'string' || !apnsToken) {
              throw new TypeError('Apple returned an invalid push notification token.')
            }

            // Firebase can reject the eager getToken call until APNs registration finishes.
            // Ask for the FCM token again at the native event boundary instead of relying on timing.
            return messaging.getToken().then(({ token }) => {
              if (typeof token !== 'string' || !token) {
                throw new TypeError('Firebase returned an invalid messaging token.')
              }
              return onToken(token)
            })
          }),
        ),
      )
    } else if (platform === 'android') {
      await localNotifications.createChannel({
        id: ALERT_CHANNEL_ID,
        name: i18n`AROYA alerts`,
        description: i18n`Facility and device alerts from AROYA`,
        importance: 4,
        vibration: true,
      })
      listeners.push(
        messaging.addListener(
          'notificationReceived',
          handleEvent(({ notification }: NotificationReceivedEvent) =>
            presentAndroidPushNotification(notification, localNotifications),
          ),
        ),
        localNotifications.addListener(
          'localNotificationActionPerformed',
          handleEvent(({ notification }: ActionPerformed) => {
            const data = assertRecord(notification.extra, 'Push notification data')
            return onNotificationAction(data)
          }),
        ),
      )
    }
    pushListeners = await Promise.all(listeners)
  }

  const { token } = await messaging.getToken()
  if (typeof token !== 'string' || !token) throw new TypeError('Firebase returned an invalid messaging token.')
  return Object.freeze({ permission, token })
}

interface DisableNativePushOptions {
  readonly messaging?: Pick<Messaging, 'deleteToken'>
  readonly requireNative?: (feature: string) => void
}

export const disableNativePush = async ({
  messaging = FirebaseMessaging,
  requireNative = requireNativePlatform,
}: DisableNativePushOptions = {}): Promise<void> => {
  requireNative('Push notifications')
  await messaging.deleteToken()
  if (!pushListeners) return
  await Promise.all(pushListeners.map(listener => listener.remove()))
  pushListeners = null
}

export const getNativeDeviceAgent = async (
  device: Pick<DevicePlugin, 'getId' | 'getInfo'> = Device,
  requireNative: (feature: string) => void = requireNativePlatform,
): Promise<readonly [string, string]> => {
  requireNative('Device identification')
  const [{ identifier }, { model }] = await Promise.all([device.getId(), device.getInfo()])
  if (typeof identifier !== 'string' || !identifier || typeof model !== 'string' || !model) {
    throw new TypeError('Native device information is incomplete.')
  }
  return Object.freeze([identifier, model] as const)
}
